// Document Center — helpers.
import type { Card, CouldntReason, DocPage } from './types';

export const DC_USER = { name: 'Ava Thompson', org: 'Lone Star Billing' } as const;
export const DC_ACTOR = `${DC_USER.name} (${DC_USER.org})`;
export const FAX_ADDRESS = 'docs.alamoheights@fax.elva.ai';

export function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function parseAmount(raw: string): number {
  const n = Number(raw.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** The reasons a person can give in "Why couldn't you charge {Payer}'s card?" (D200). */
export const COULDNT_REASONS: { value: Exclude<CouldntReason, 'expired_unused'>; label: string }[] = [
  { value: 'used', label: 'Declined: the card was already used' },
  { value: 'expired', label: 'Declined: the card has expired' },
  { value: 'amount', label: 'The amount on the card doesn’t match' },
  { value: 'other', label: 'Something else' },
];

/** The reason in a sentence, lower case, for the warning card's heading. */
export function reasonPhrase(reason: CouldntReason, note?: string): string {
  switch (reason) {
    case 'used': return 'declined: the card was already used';
    case 'expired': return 'declined: the card has expired';
    case 'amount': return 'the amount on the card doesn’t match';
    case 'expired_unused': return 'the card expired before it was charged';
    case 'other': return note ? `something else: ${note}` : 'something else';
  }
}

/** "the payment" / "both payments" / "all 3 payments" */
export function paymentsWord(n: number): string {
  return n === 1 ? 'the payment' : n === 2 ? 'both payments' : `all ${String(n)} payments`;
}

export const BLACK = '\u2588';
/** The card number as solid bars, ending in the last 4 (the only digits ELVA keeps). */
export function maskedCardNumber(last4: string): string {
  return `${BLACK.repeat(4)} ${BLACK.repeat(4)} ${BLACK.repeat(4)} ${last4}`;
}

/** Where to find the original card page: the fax in the fax inbox, or the payer's portal (D216). */
export function cardWhereToFind(card: Card): string {
  const src = card.source;
  return src.kind === 'fax'
    ? `The fax from ${card.payer} in your fax inbox · ${src.time}`
    : `${card.payer}’s portal › Payments · ${src.ref}`;
}

/**
 * The faxed card page as plain text: always the redacted copy (D216). The card number
 * is solid bars ending in the last 4, the security code is a bar. ELVA never keeps the
 * number, so no other digits appear.
 */
export function cardFaxPage(card: Card): DocPage {
  return {
    alt: `The faxed virtual-card page from ${card.payer}. The card number is blacked out except the last 4 digits, ending ${card.last4}, and the security code is blacked out`,
    caption: 'Page 1 of 1 · card details removed when the fax arrived',
    text: `${card.payer.toUpperCase()} VIRTUAL CARD PAYMENT\nPAYEE: ALAMO HEIGHTS DENTAL\nAMOUNT: ${money(card.amount)}\nCARD NUMBER: ${maskedCardNumber(card.last4)}\nEXP: ${card.expires}   CVV: ${BLACK.repeat(3)}`,
  };
}

/**
 * D252: until ELVA's PCI self-assessment is signed, ELVA keeps no virtual-card page at all, so no card
 * shows a thumbnail. `?pci=done` (mock only, read at render, not persisted) shows the redacted thumbnail.
 */
function readPci(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('pci')?.toLowerCase() === 'done';
  } catch {
    return false;
  }
}
// Read once when the app loads, so in-app navigation (which drops the query) keeps it; not stored anywhere,
// so a reload without `?pci=done` is back to the default. It is also read at render, so a reload with it works.
const PCI_AT_LOAD = readPci();
export function pciDone(): boolean {
  return PCI_AT_LOAD || readPci();
}
